import React, { useState } from "react";
import { useHome } from "../../providers/HomeContext";
import { useCursor } from "../../providers/CursorContext";

import instagramIcon from "../../assets/images/instagram.webp";
import linkedinIcon from "../../assets/images/linkedin.webp";
import dribbbleIcon from "../../assets/images/dribbble.webp";
import githubIcon from "../../assets/images/github.webp";

interface SocialMediaItem {
  name: string;
  handle: string;
  icon: string;
  url: string;
}

const SOCIAL_ITEMS: SocialMediaItem[] = [
  {
    name: "Instagram",
    handle: "@yasfdany",
    icon: instagramIcon,
    url: "https://instagram.com/yasfdany",
  },
  {
    name: "LinkedIn",
    handle: "yasfdany",
    icon: linkedinIcon,
    url: "https://www.linkedin.com/in/yasfdany/",
  },
  {
    name: "Dribbble",
    handle: "yasfdany",
    icon: dribbbleIcon,
    url: "https://dribbble.com/yasfdany",
  },
  {
    name: "GitHub",
    handle: "yasfdany",
    icon: githubIcon,
    url: "https://github.com/yasfdany",
  },
];

export const SocialMediaSection: React.FC = () => {
  const { currentPage } = useHome();
  const { setCursorSize, setHover } = useCursor();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const isActive = currentPage === 2;

  const handleIconClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <div className="w-full h-full flex items-center justify-center relative select-none">
      {/* Dynamic expanding circle backdrop */}
      <div
        className={`absolute rounded-full bg-primary dark:bg-[#5C6286] transition-all duration-700 ease-out z-0`}
        style={{
          width: "150vmax",
          height: "150vmax",
          transform: isActive ? "scale(1)" : "scale(0)",
          opacity: isActive ? 1 : 0,
        }}
      />

      {/* Desktop view: Horizontal Row of Icon Cards */}
      <div className="relative z-10 hidden md:flex items-center justify-center gap-6 md:gap-12 px-6 max-w-4xl w-full">
        {SOCIAL_ITEMS.map((item, idx) => {
          const isItemHovered = hoveredIndex === idx;

          return (
            <div
              key={idx}
              onClick={() => handleIconClick(item.url)}
              onMouseEnter={() => {
                setHoveredIndex(idx);
                setHover(true);
                setCursorSize(48);
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
                setHover(false);
                setCursorSize(24);
              }}
              className={`cursor-pointer transition-all duration-300 select-none flex items-center justify-center border-2 rounded-2xl w-20 h-20 md:w-28 md:h-28 active:scale-90 relative ${
                isItemHovered
                  ? "bg-white border-white shadow-xl shadow-black/10"
                  : "bg-transparent border-transparent"
              }`}
              style={{
                transform: isItemHovered
                  ? "scale(1.2) translateY(-10px) rotate(3deg)"
                  : "scale(1) translateY(0) rotate(0)",
              }}
            >
              {/* Inactive Icon (White) */}
              <img
                src={item.icon}
                alt=""
                className={`w-8 h-8 md:w-12 md:h-12 object-contain absolute transition-opacity duration-300 ${
                  isItemHovered ? "opacity-0" : "opacity-100"
                }`}
                style={{
                  filter: "brightness(0) invert(1)",
                }}
              />
              
              {/* Hovered Icon (Brand Color) */}
              <img
                src={item.icon}
                alt={item.name}
                className={`w-8 h-8 md:w-12 md:h-12 object-contain absolute transition-opacity duration-300 ${
                  isItemHovered ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  filter:
                    idx === 0
                      ? "invert(24%) sepia(85%) saturate(2311%) hue-rotate(224deg) brightness(91%) contrast(93%)" // instagram #515bd4
                      : idx === 1
                      ? "invert(36%) sepia(87%) saturate(541%) hue-rotate(164deg) brightness(92%) contrast(89%)" // linkedin #2678b7
                      : idx === 2
                      ? "invert(46%) sepia(50%) saturate(1637%) hue-rotate(309deg) brightness(99%) contrast(92%)" // dribbble #ea4c89
                      : "invert(64%) sepia(21%) saturate(2421%) hue-rotate(85deg) brightness(94%) contrast(83%)", // github #37ba4e
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Mobile view: Vertical Stack of Detail Cards */}
      <div className="relative z-10 flex md:hidden flex-col gap-4 max-w-xl w-full px-6">
        {SOCIAL_ITEMS.map((item, idx) => {
          const isItemHovered = hoveredIndex === idx;

          return (
            <div
              key={idx}
              onClick={() => handleIconClick(item.url)}
              onMouseEnter={() => {
                setHoveredIndex(idx);
                setHover(true);
                setCursorSize(48);
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
                setHover(false);
                setCursorSize(24);
              }}
              className={`cursor-pointer transition-all duration-300 select-none flex items-center justify-between border rounded-2xl p-4 active:scale-[0.98] relative ${
                isItemHovered
                  ? "bg-neutral-50 border-neutral-100 shadow-xl shadow-black/10 text-neutral-900"
                  : "bg-white border-white shadow-md shadow-black/5 text-neutral-800"
              }`}
              style={{
                transform: isItemHovered
                  ? "scale(1.03) translateY(-4px)"
                  : "scale(1) translateY(0)",
              }}
            >
              <div className="flex items-center gap-4">
                {/* Icon Container */}
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center relative shrink-0 transition-colors duration-300 ${
                    isItemHovered ? "bg-neutral-200" : "bg-neutral-100"
                  }`}
                >
                  {/* Inactive Icon (Dark Grey) */}
                  <img
                    src={item.icon}
                    alt=""
                    className={`w-6 h-6 object-contain absolute transition-opacity duration-300 ${
                      isItemHovered ? "opacity-0" : "opacity-100"
                    }`}
                    style={{
                      filter: "invert(13%) sepia(0%) saturate(614%) hue-rotate(166deg) brightness(94%) contrast(87%)", // #2A2A2A
                    }}
                  />
                  
                  {/* Hovered Icon (Brand Color) */}
                  <img
                    src={item.icon}
                    alt={item.name}
                    className={`w-6 h-6 object-contain absolute transition-opacity duration-300 ${
                      isItemHovered ? "opacity-100" : "opacity-0"
                    }`}
                    style={{
                      filter:
                        idx === 0
                          ? "invert(24%) sepia(85%) saturate(2311%) hue-rotate(224deg) brightness(91%) contrast(93%)" // instagram #515bd4
                          : idx === 1
                          ? "invert(36%) sepia(87%) saturate(541%) hue-rotate(164deg) brightness(92%) contrast(89%)" // linkedin #2678b7
                          : idx === 2
                          ? "invert(46%) sepia(50%) saturate(1637%) hue-rotate(309deg) brightness(99%) contrast(92%)" // dribbble #ea4c89
                          : "invert(64%) sepia(21%) saturate(2421%) hue-rotate(85deg) brightness(94%) contrast(83%)", // github #37ba4e
                    }}
                  />
                </div>

                {/* Text details */}
                <div className="flex flex-col text-left font-nunito">
                  <span className="font-bold text-lg leading-tight">
                    {item.name}
                  </span>
                  <span className={`text-xs mt-0.5 transition-colors duration-300 ${
                    isItemHovered ? "text-neutral-500" : "text-neutral-400"
                  }`}>
                    {item.handle}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
